import { Index, Show, createMemo, createSignal, type Accessor } from 'solid-js';
import type { GpuDeviceView, LoadedModelView } from '../state/metrics';
import { boundLabel, thermalHeadroomC } from './gpu-derive';
import { llamacppVramForDevice } from './llamacpp-vram-derive';
import { InfoTip } from '../ui/info-tip';
import { ArcRing } from '../ui/rings';
import { HudTabs } from '../ui/hud-tabs';

const TIPS = {
  bound:
    'The GPU is spending more time moving data than doing math — normal for AI models. It means faster memory would help more than extra cores.',
  vram:
    "VRAM is the graphics card's own fast memory. A model has to fit in here to run at full speed; whatever doesn't fit falls back to slower system RAM.",
  temp:
    'Current temperature and how far it is from the slowdown point. At the limit the card deliberately slows itself down to stay safe.',
  power:
    "How much power the card is drawing vs the most it's allowed. Laptops set a low ceiling, so the card often hits this limit — and slows down — long before it ever gets hot.",
  clock:
    "The card's current speed vs its maximum. It drops below the max when the card is throttling to stay within its power or heat limit.",
  processes: 'Processes using this GPU and their video-memory usage.',
} as const;

function gb(mb: number): string {
  return (Math.round((mb / 1024) * 10) / 10).toFixed(1);
}

function num(n: number): string {
  return n.toLocaleString('en-US');
}

function Meter(props: { fillPct: number }) {
  return (
    <div class="hud-clip h-1.5 bg-track">
      <div
        class="h-full transition-[width] duration-500"
        style={{ width: `${Math.min(100, Math.max(0, props.fillPct))}%`, background: 'var(--holo-deep)' }}
      />
    </div>
  );
}

function MetricRow(props: { label: string; tip: string; value: import('solid-js').JSX.Element; meterPct?: number }) {
  return (
    <div class="mb-3 last:mb-0">
      <div class="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
        <span class="hud-mono inline-flex items-center gap-1 text-[0.625rem] uppercase tracking-[0.18em] text-faint">
          {props.label} <InfoTip text={props.tip} />
        </span>
        <span class="font-semibold tabular-nums">{props.value}</span>
      </div>
      <Show when={props.meterPct != null}>
        <Meter fillPct={props.meterPct!} />
      </Show>
    </div>
  );
}

export function GpuCluster(props: {
  devices: Accessor<GpuDeviceView[]>;
  loaded: Accessor<LoadedModelView[]>;
  serverPid: Accessor<number | null>;
  /**
   * Whether a GPU frame has ever arrived. Optional and additive: every device-driven
   * call site (including all pre-existing tests) always has real data by construction,
   * so omitting this prop keeps that "we know" default. Only the dashboard's top-level
   * Layout — which can render before the first `gpu` frame lands — passes `false` to
   * tell an honest "waiting for telemetry" apart from a poll that positively found zero
   * GPUs.
   */
  hasFrame?: Accessor<boolean>;
  /**
   * `false` = the described llama.cpp server has no resolvable PID and
   * name-matching would credit other processes; the llama.cpp share is then
   * omitted rather than guessed. Defaults to true — the pre-discovery
   * single-server behaviour.
   */
  attributeLlamacpp?: Accessor<boolean>;
}) {
  const [pickedIndex, setPickedIndex] = createSignal<number | null>(null);

  /** Default device: the first discrete card, else the first one reported. */
  const device = createMemo<GpuDeviceView | null>(() => {
    const list = props.devices();
    if (!list.length) return null;
    const picked = pickedIndex();
    if (picked != null) {
      const hit = list.find((d) => d.index === picked);
      if (hit) return hit;
    }
    return list.find((d) => !d.vramShared) ?? list[0]!;
  });

  /**
   * A shared-memory device (e.g. an integrated GPU) never gets a `vramTotalMb` from the
   * driver — there is no dedicated pool to measure. Rendering a "0.0 / 0.0 GB" split for
   * that case would state a measurement that was never taken, so the whole VRAM block is
   * gated on this instead of `?? 0`-coalescing the missing total away.
   *
   * Both `vramTotalMb` and `vramUsedMb` are required, not just the total: AMD reads them
   * from separate sysfs files, so a card that answered one and not the other must not
   * render a "0.0 / 8.0 GB · 8.0 free" split — that invents a 0 for the half that never
   * arrived and a "free" figure derived from it.
   */
  const hasVram = () => {
    const dev = device();
    return dev != null && !dev.vramShared && dev.vramTotalMb != null && dev.vramUsedMb != null;
  };
  const vramTotal = () => device()?.vramTotalMb ?? 0;
  const vramUsed = () => device()?.vramUsedMb ?? 0;
  const vramFree = () => Math.max(0, vramTotal() - vramUsed());
  // `attributeLlamacpp === false` means the described server has no resolvable
  // PID (a selected remote/configured target, or an unresolved router child) —
  // `llamacppVramForDevice`'s fallback would otherwise match by process NAME and
  // credit some other `llama-server` on the box to it. Omitted rather than
  // guessed; defaults to true (the pre-discovery single-server behaviour) when
  // the prop isn't passed at all.
  const llamaShare = () =>
    props.attributeLlamacpp?.() === false ? 0 : llamacppVramForDevice(device(), props.serverPid());
  /**
   * Ollama's per-model VRAM (`/api/ps`) is host-wide — it names no device. With exactly
   * one GPU that attribution is unambiguous, so it's safe to fold in. With two or more
   * cards, crediting it to whichever card the tabs happen to have selected would put a
   * model loaded on card A on card B's meter; leave it out and let it fall into "other"
   * (used-but-unattributed) instead of inventing a per-device split.
   */
  const modelInVram = () => {
    const ollamaShare = props.devices().length === 1 ? props.loaded().reduce((sum, m) => sum + m.vramMb, 0) : 0;
    return Math.min(vramUsed(), ollamaShare + llamaShare());
  };
  const otherUsed = () => Math.max(0, vramUsed() - modelInVram());
  const segPct = (mb: number) => (vramTotal() > 0 ? Math.min(100, Math.max(0, (mb / vramTotal()) * 100)) : 0);

  const bound = () => boundLabel(device()?.utilPct ?? null, device()?.memUtilPct ?? null);
  const throttling = () => (device()?.throttleReasons.length ?? 0) > 0;
  const headroom = () => thermalHeadroomC(device()?.tempC ?? null, device()?.tempSlowdownC ?? null);

  const tabs = () =>
    props.devices().map((d) => ({
      id: String(d.index),
      label: d.name,
      detail: d.utilPct != null ? `· ${d.utilPct}%` : undefined,
    }));

  // `hasFrame` defaults to "known" (see the prop doc above) — only an explicit
  // `false` from Layout means "no gpu frame has arrived yet", distinct from a frame
  // that positively reported zero devices.
  const noFrameYet = () => props.hasFrame?.() === false;

  return (
    <Show
      when={device()}
      fallback={
        <p class="text-center text-sm text-muted">
          {noFrameYet() ? 'waiting for GPU telemetry…' : 'no GPU detected'}
        </p>
      }
    >
      {(d) => (
        <div>
          <Show when={props.devices().length > 1}>
            <HudTabs
              items={tabs()}
              selected={String(d().index)}
              onSelect={(id) => setPickedIndex(Number(id))}
              label="Graphics cards"
            />
          </Show>

          <div class="mb-3.5 flex flex-wrap items-center gap-2">
            <span class="hud-mono text-sm font-semibold uppercase tracking-[0.14em]">{d().name}</span>
            <span class="hud-clip hud-mono border border-line bg-accent-soft px-2.5 py-0.5 text-[0.6563rem] text-muted">
              {d().vendor.toUpperCase()}
              <Show when={d().driverVersion}>{(v) => <> · drv {v()}</>}</Show>
            </span>
            <Show when={bound()}>
              {(label) => (
                <>
                  <span class="hud-clip hud-mono border border-warn/40 bg-warn-soft px-2.5 py-0.5 text-[0.6563rem] text-warn">
                    {label()}
                  </span>
                  <InfoTip text={TIPS.bound} />
                </>
              )}
            </Show>
          </div>

          <div class="flex items-center gap-6 max-[500px]:gap-4">
            <ArcRing value={d().utilPct} label="GPU util" />

            <div class="min-w-0 flex-1">
              <div class="mb-3">
                <Show
                  when={hasVram()}
                  fallback={
                    <>
                      <span class="hud-mono inline-flex items-center gap-1 text-[0.625rem] uppercase tracking-[0.18em] text-faint">
                        VRAM <InfoTip text={TIPS.vram} />
                      </span>
                      <p class="mt-1 text-xs text-muted">
                        {d().vramShared
                          ? 'This card shares system memory — no dedicated VRAM figure to report.'
                          : "This card's VRAM usage wasn't reported."}
                      </p>
                    </>
                  }
                >
                  <div class="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
                    <span class="hud-mono inline-flex items-center gap-1 text-[0.625rem] uppercase tracking-[0.18em] text-faint">
                      VRAM <InfoTip text={TIPS.vram} />
                    </span>
                    <span class="font-semibold tabular-nums">
                      {gb(vramUsed())} / {gb(vramTotal())} GB · <span class="text-good">{gb(vramFree())} free</span>
                    </span>
                  </div>
                  <div
                    class="flex h-1.5 gap-0.5"
                    role="img"
                    aria-label={`VRAM: models ${modelInVram()} MB, other ${otherUsed()} MB, free ${vramFree()} MB`}
                  >
                    <span data-seg="model" class="h-full bg-holo-deep" style={{ width: `${segPct(modelInVram())}%` }} />
                    <span data-seg="other" class="h-full bg-faint" style={{ width: `${segPct(otherUsed())}%` }} />
                    <span data-seg="free" class="h-full bg-track" style={{ width: `${segPct(vramFree())}%` }} />
                  </div>
                  <div class="hud-mono mt-1.5 flex flex-wrap gap-3.5 text-[0.5938rem] uppercase text-faint">
                    <span class="inline-flex items-center gap-1.5">
                      <b class="h-2 w-2 bg-holo-deep" /> models {gb(modelInVram())}
                    </span>
                    <span class="inline-flex items-center gap-1.5">
                      <b class="h-2 w-2 bg-faint" /> other {gb(otherUsed())}
                    </span>
                    <span class="inline-flex items-center gap-1.5">
                      <b class="h-2 w-2 bg-track outline outline-1 outline-line" /> free {gb(vramFree())} GB
                    </span>
                  </div>
                </Show>
              </div>

              <MetricRow
                label="Temp"
                tip={TIPS.temp}
                meterPct={d().tempC != null ? (d().tempC! / 90) * 100 : undefined}
                value={
                  <>
                    {d().tempC ?? '—'} °C
                    <small class="font-normal text-faint">
                      {throttling()
                        ? ` · throttle: ${d().throttleReasons.join(', ')}`
                        : headroom() != null
                          ? ` · headroom ${headroom()}°`
                          : ''}
                    </small>
                  </>
                }
              />

              <MetricRow
                label="Power"
                tip={TIPS.power}
                meterPct={
                  d().powerW != null && d().powerLimitW ? (d().powerW! / d().powerLimitW!) * 100 : undefined
                }
                value={
                  <>
                    {d().powerW ?? '—'}
                    <Show when={d().powerLimitW != null} fallback=" W">
                      {' '}/ {d().powerLimitW} W
                    </Show>
                  </>
                }
              />

              <MetricRow
                label="Clock"
                tip={TIPS.clock}
                value={
                  <>
                    {d().clockCoreMhz != null ? num(d().clockCoreMhz!) : '—'}
                    <small class="font-normal text-faint">
                      <Show when={d().clockCoreMaxMhz != null}>
                        {' '}/ {num(d().clockCoreMaxMhz!)} MHz
                      </Show>
                    </small>
                  </>
                }
              />
            </div>
          </div>

          <Show when={d().processes.length > 0}>
            <details class="mt-2 border-t border-line pt-1">
              <summary class="hud-mono cursor-pointer list-none py-2 text-[0.6563rem] uppercase tracking-[0.14em] text-holo opacity-85">
                processes on GPU · {d().processes.length} <InfoTip text={TIPS.processes} />
              </summary>
              <ul class="flex flex-col gap-1">
                <Index each={d().processes}>
                  {(p) => (
                    <li class="flex justify-between border-b border-line/50 py-1.5 text-xs last:border-b-0">
                      <span class="text-ink">{p().name}</span>
                      <span class="tabular-nums text-muted">
                        {p().usedVramMb != null ? `${num(p().usedVramMb!)} MB` : '—'}
                      </span>
                    </li>
                  )}
                </Index>
              </ul>
            </details>
          </Show>
        </div>
      )}
    </Show>
  );
}
